import _ from "lodash";
import clsx from "clsx";
import React from "react";
import LaunchOutlinedIcon from "@material-ui/icons/LaunchOutlined";
import moment from "moment-timezone";
import { Drawer, Tooltip } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/CloseOutlined";
import WarningIcon from "@material-ui/icons/Warning";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import ReactJson from "react-json-view";

import { isValidUrl } from "../util";
import { ValueIcon } from "./ValueIcons";

import { FilterIcon } from "../components/icons/themes/windows10";
import { DefineEvent, ViewEvent } from "./shared";

const useStyles = makeStyles((theme) => ({
  drawerExpanded: {
    boxShadow: "-4px 0 150px 0px rgb(100 100 100 / 85%)",
    width: 350,
    backgroundColor: "white",
    padding: theme.spacing(2),
  },
  drawerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #CCC",
    paddingBottom: "8px",
  },
  drawerCloseButton: {
    borderRadius: 3,
    color: "#546e7a",
    width: 28,
    height: 28,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    transition: "all .25s",
    "&:hover": {
      backgroundColor: "rgba(30, 50, 56, .08)",
      color: "#546e7a",
    },
  },
  eventPropsCategory: {
    textTransform: "uppercase",
    color: "#AAA",
    fontWeight: "bold",
    fontSize: 12,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  eventPropsKey: {
    textAlign: "left",
    color: "#444",
    fontSize: "14px",
  },
  eventPropsVal: {
    marginBottom: theme.spacing(2),
    textAlign: "left",
    fontSize: "14px",
    color: "#222",
    wordBreak: "break-all",
    "& a": {
      "&, &:link, &:visited, &:hover": {
        color: "#3f51b5",
        textDecoration: "none",
      },
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },
  eventPropsValLinkIcon: {
    color: "#3f51b5",
    textDecoration: "none",
    fontSize: 12,
    marginRight: theme.spacing(0.5),
    position: "relative",
    top: 2,
  },
  eventPropsFilterIcon: {
    verticalAlign: "middle",
    marginLeft: theme.spacing(0.5),
    cursor: "pointer",
  },
  eventPropsValSingleLine: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    wordBreak: "normal",
  },
  jsonListElement: {
    verticalAlign: "top",
    display: "inline-block",
    cursor: "zoom-out",
    maxWidth: "100%",
    overflow: "scroll",
  },
  jsonListElementClosed: {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    cursor: "zoom-in",
  },
  jsonListElementArrow: {
    fontSize: 12,
    color: "#666",
    position: "relative",
    top: 2,
  },
  bold: {
    fontWeight: 600,
  },
  fontMonospace: {
    fontFamily: "monospace",
  },
}));

export const canDefineOrView = (event) =>
  event.type.length > 0 && event.type[0] !== "$";

export const ObjectEventProperty = ({ value }) => {
  const classes = useStyles();

  return (
    <pre className={clsx(classes.jsonListElement, classes.fontMonospace)}>
      <ReactJson
        src={value}
        indentWidth={2}
        collapsed={1}
        displayDataTypes={false}
        name={null}
        groupArraysAfterLength={5}
      />
    </pre>
  );
};

ObjectEventProperty.propTypes = {
  value: PropTypes.any,
};

export const EventPropValue = ({ value, builtIn }) => {
  const classes = useStyles();

  if (value === null) {
    return (
      <Tooltip title="You sent the value `null`">
        <span className={classes.fontMonospace}>
          <WarningIcon className={classes.propIcon} />
          null
        </span>
      </Tooltip>
    );
  } else if (value === undefined) {
    return (
      <Tooltip title="You sent the value `undefined`">
        <span className={classes.fontMonospace}>
          <WarningIcon className={classes.propIcon} />
          undefined
        </span>
      </Tooltip>
    );
  } else if (typeof value == "string") {
    if (isValidUrl(value)) {
      const url = new URL(value);
      return (
        <>
          <LaunchOutlinedIcon className={classes.eventPropsValLinkIcon} />
          <a target="_blank" rel="noopener noreferrer" href={url.href}>
            {url.href}
          </a>
        </>
      );
    }

    if (builtIn && value.charAt(0) === "$") {
      // This is a built in value for a built-in prop.
      value = _.startCase(value.slice(1));
    }

    if (!builtIn && value.length === 0) {
      return (
        <Tooltip title="You sent an empty string">
          <span className={classes.fontMonospace}>
            <WarningIcon className={classes.propIcon} />
            &quot;&quot;
          </span>
        </Tooltip>
      );
    }
  } else if (typeof value === "object") {
    value = <ObjectEventProperty value={value} />;
  } else {
    value = <span>{value.toString()}</span>;
  }

  return value;
};

EventPropValue.propTypes = {
  value: PropTypes.any,
  builtIn: PropTypes.bool.isRequired,
};

export const EventPropName = ({ name, builtIn }) => {
  if (builtIn) {
    name = _.startCase(name);

    name = name.replace(/(\b)id(\b)/gi, "$1ID$2");
    name = name.replace(/(\b)ids(\b)/gi, "$1IDs$2");
    name = name.replace(/(\b)url(\b)/gi, "$1URL$2");
    name = name.replace(/(\b)UTM(\b)/gi, "$1UTM$2");
    name = name.replace(/(\b)CSS(\b)/gi, "$1CSS$2");

    if (name.charAt(0) === "$") {
      // This is a name like `$host` for a built-in prop.
      return _.startCase(name.slice(1));
    }
  }

  return <span>{name}</span>;
};

EventPropName.propTypes = {
  name: PropTypes.string.isRequired,
  builtIn: PropTypes.bool.isRequired,
};

const SINGLE_LINE_PROPS = ["url", "referrer"];

const ADD_FILTER_PROPS = {
  user_id: "$user_id",
  host: "$host",
  device_id: "$device_id",
  domain: "$domain",
  browser: "$browser",
  path: "$pathname",
  href: "$el_attr__href",
};

export const EventProp = ({
  handleAddFilter,
  name,
  value,
  builtIn = false,
}) => {
  const classes = useStyles();
  const forceSingleLine = _.includes(SINGLE_LINE_PROPS, name.toLowerCase());
  const allowOneClickFilter = !!ADD_FILTER_PROPS[name];

  const addFilter = () => {
    handleAddFilter(ADD_FILTER_PROPS[name], "=", value);
  };

  return (
    <div>
      <dt className={clsx(classes.bold, classes.eventPropsKey)}>
        <EventPropName builtIn={builtIn} name={name} />
        {allowOneClickFilter && (
          <Tooltip title="Quick Add Filter">
            <FilterIcon
              className={classes.eventPropsFilterIcon}
              height={15}
              width={15}
              onClick={addFilter}
            />
          </Tooltip>
        )}
      </dt>
      <dd
        className={clsx(
          classes.eventPropsVal,
          forceSingleLine && classes.eventPropsValSingleLine
        )}
      >
        <ValueIcon name={name} value={value} />
        <EventPropValue builtIn={builtIn} value={value} />
      </dd>
    </div>
  );
};

EventProp.propTypes = {
  handleAddFilter: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.any,
  builtIn: PropTypes.bool,
};

const PropList = ({ handleAddFilter, propsList, builtIn = false }) => {
  return (
    <>
      {_(propsList)
        .keys()
        .filter((key) => propsList[key] !== undefined)
        .map((key, index) => (
          <EventProp
            handleAddFilter={handleAddFilter}
            name={key}
            value={propsList[key]}
            key={index}
            builtIn={builtIn}
          />
        ))
        .value()}
    </>
  );
};

PropList.propTypes = {
  handleAddFilter: PropTypes.func.isRequired,
  propsList: PropTypes.object.isRequired,
  builtIn: PropTypes.bool,
};

export function EventDetails({
  eventDrilldownData,
  setEventDrilldownData,
  handleAddFilter,
}) {
  const classes = useStyles();

  if (!eventDrilldownData || typeof eventDrilldownData["id"] === "undefined") {
    return null;
  }

  const customProps = eventDrilldownData.custom_properties || {};
  const builtInProps = eventDrilldownData.built_in_properties || {};
  if (typeof eventDrilldownData["type"] !== "undefined") {
    if (_.includes(["click", "submit", "change"], eventDrilldownData.type)) {
      if (typeof eventDrilldownData["type_specific_property"] !== "undefined") {
        builtInProps["target CSS selector"] =
          eventDrilldownData["type_specific_property"];
      }
    }
  }
  if (typeof eventDrilldownData["time"] !== "undefined") {
    builtInProps.time = moment.unix(eventDrilldownData.time).format("LLL");
  }

  return (
    <Drawer
      anchor="right"
      open={true}
      variant="permanent"
      classes={{ paper: classes.drawerExpanded }}
    >
      <div className={clsx(classes.drawerHeader)}>
        {canDefineOrView(eventDrilldownData) ? (
          eventDrilldownData.event_def_id ? (
            <ViewEvent eventDefID={eventDrilldownData.event_def_id} />
          ) : (
            <DefineEvent
              type={eventDrilldownData.type}
              typeSpecificProperty={eventDrilldownData.type_specific_property}
            />
          )
        ) : (
          <div />
        )}
        <div
          className={clsx(classes.drawerCloseButton)}
          onClick={() => {
            setEventDrilldownData(null);
          }}
        >
          <CloseIcon />
        </div>
      </div>

      {!_.isEmpty(customProps) && (
        <div>
          <div className={clsx(classes.eventPropsCategory)}>
            Custom Properties
          </div>
          <PropList propsList={customProps} />
        </div>
      )}

      <div>
        <div className={clsx(classes.eventPropsCategory)}>
          Built-In Properties
        </div>
        <PropList
          handleAddFilter={handleAddFilter}
          propsList={builtInProps}
          builtIn={true}
        />
      </div>
    </Drawer>
  );
}

EventDetails.propTypes = {
  eventDrilldownData: PropTypes.object,
  setEventDrilldownData: PropTypes.func.isRequired,
  handleAddFilter: PropTypes.func.isRequired,
};
